import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { memo } from 'react'

import {
  BoundaryEventShapes,
  GraphColors,
  NodeLabel as NodeLabelStyles,
} from '../../../bpmn-graph.constants'
import { BoundaryEventNodeData } from '../bpmn-graph.mapper'
import { getNodeVisuals, recolorIconSvg, recolorShapeSvg } from '../node-visuals'
import { useRefreshHandleBounds } from '../use-refresh-handle-bounds'
import {
  CONNECT_SOURCE_HANDLE_ID,
  ConnectSourceIcon,
  connectSourceHandleStyle,
} from './BpmnFlowNode'

function BoundaryEventNode({ id, data, selected }: NodeProps<Node<BoundaryEventNodeData>>) {
  const { boundaryEvent, readonly, auditStyles } = data
  const visuals = getNodeVisuals(boundaryEvent.type)
  useRefreshHandleBounds(id, readonly ? 'readonly' : 'editable')

  // NON_INTERRUPTING_STATEFUL: the dashed ring gains a solid outer
  // ring while the boundary event is selected
  const stateShape =
    selected && visuals.shape === BoundaryEventShapes.NON_INTERRUPTING
      ? BoundaryEventShapes.NON_INTERRUPTING_SELECTED
      : (visuals.shape ?? '')
  // Audit palette overrides readonly, same rationale as BpmnFlowNode.
  const auditIconColor = auditStyles?.defaultIconColor ?? auditStyles?.defaultColor
  const shapeSvg = auditStyles
    ? recolorShapeSvg(stateShape, auditStyles.defaultColor, auditStyles.neutralColor)
    : readonly
      ? recolorShapeSvg(stateShape, GraphColors.neutral_500_90, GraphColors.neutral_50)
      : stateShape
  const iconSvg = auditStyles
    ? recolorIconSvg(visuals.icon ?? '', auditIconColor)
    : readonly
      ? recolorIconSvg(visuals.icon ?? '', GraphColors.neutral_500_90)
      : visuals.icon

  return (
    <div
      data-testid={`boundary-event-${boundaryEvent.id}`}
      data-boundary-type={boundaryEvent.type}
      style={{
        width: visuals.width,
        height: visuals.height,
        position: 'relative',
        cursor: 'pointer',
        // Active shadow: hard blue bar shadowHeight above the shape
        filter: selected ? `drop-shadow(0 -4px 0 ${GraphColors.blue_500})` : undefined,
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{
          opacity: 0,
          width: 1,
          height: 1,
          minWidth: 0,
          minHeight: 0,
          border: 'none',
          background: 'transparent',
        }}
      />
      {!readonly && (
        <Handle
          id={CONNECT_SOURCE_HANDLE_ID}
          type="source"
          position={Position.Bottom}
          isConnectableEnd={false}
          className="bpmn-connect-source-handle"
          data-testid={`boundary-event-connect-${boundaryEvent.id}`}
          // RF's own handle CSS (bottom: 0 + translate(-50%, 50%)) centres
          // the icon on the border — half inside the node, half outside
          style={connectSourceHandleStyle}
        >
          <ConnectSourceIcon />
        </Handle>
      )}
      <div dangerouslySetInnerHTML={{ __html: shapeSvg }} />
      {iconSvg && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          dangerouslySetInnerHTML={{ __html: iconSvg }}
        />
      )}
      {selected && boundaryEvent.name && (
        <div
          data-testid={`boundary-event-label-${boundaryEvent.id}`}
          style={{
            position: 'absolute',
            bottom: visuals.height + 10,
            left: '50%',
            transform: 'translateX(-50%)',
            background: GraphColors.blue_500,
            color: GraphColors.white,
            fontSize: NodeLabelStyles.fontSize,
            fontFamily: NodeLabelStyles.fontFamily,
            borderRadius: 8,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {boundaryEvent.name}
        </div>
      )}
    </div>
  )
}

export default memo(BoundaryEventNode)
